import '../../../styles/pages.css'

import { Box, Button } from '@mui/material'
import { ISchoolCopy } from 'infrastructure/api'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { CurriculaSelection } from '../../tree/CurriculaSelection'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useGetCurriculumInDistrict } from '../../tree/TreeActions'
import { LockedSchoolsIds } from '../../utility/utils'
import {
  addCurricula,
  getcurriculumsAddedOnSchoolLevel,
  getTKATEventInfo,
} from '../../../providers/mutations'
import { AddCurricula } from '../../../providers/resources'
import RequestDialog from '../../dialogs/RequestDialog'
import { useNotify } from 'shared/ui/Notify'

export default function AddCurriculum({
  districtID,
  schoolDistrictID,
  schoolID,
  addCurriculumOverride,
  onAdded,
}: {
  districtID?: number
  schoolDistrictID?: number
  schoolID?: number
  addCurriculumOverride?: (masterTitle: string, productId: number) => void
  onAdded: () => void
}) {
  const getDistrictCurricula = useGetCurriculumInDistrict()
  const [showSelection, setShowSelection] = useState(false)
  const [open, setOpen] = useState(false)
  const [currentDialog, setCurrentDialog] = useState<ReactElement | null>()
  const [curriculaMap, setCurriculaMap] = useState(new Map<string, number>())
  const [excluded, setExcluded] = useState<string[]>([])
  const [curriculaProcessing, setCurriculaProcessing] = useState(false)
  const checkProcessCompletion = 30000
  const popUpText =
    schoolID || addCurriculumOverride
      ? 'Are you sure you want to add the curriculum to the school?'
      : 'This change will affect all the schools inside the district. Are you sure you want to continue?'

  const tkatEventCheck = async () => {
    if (districtID) {
      const tkatEvent = await getTKATEventInfo(
        districtID.toString(),
        AddCurricula.key,
        'district',
        true,
      )
      setCurriculaProcessing(tkatEvent.length > 0)
    } else if (schoolID) {
      const tkatEvent = await getTKATEventInfo(
        schoolID.toString(),
        AddCurricula.key,
        'school',
        true,
      )
      setCurriculaProcessing(tkatEvent.length > 0)
    }
  }

  useEffect(() => {
    const fetchDistrictCurriculum = async () => {
      if (districtID) {
        const curriculaInSchool =
          await getcurriculumsAddedOnSchoolLevel(districtID)
        if (curriculaInSchool) {
          const excluded = curriculaInSchool.map((dc) => dc.tree_id)
          setExcluded(excluded)
        }
      }
      if (schoolDistrictID) {
        const districtCurricula = await getDistrictCurricula(schoolDistrictID)
        const reducedCurriculaArr: ISchoolCopy[] = []
        for (const curricula of districtCurricula) {
          if (!curricula.deleted) {
            reducedCurriculaArr.push(curricula)
          }
        }
        const excluded = reducedCurriculaArr.map((dc) => dc.tree_id)
        setExcluded(excluded)
      }
    }
    fetchDistrictCurriculum()
    tkatEventCheck()
  }, [districtID, schoolID, schoolDistrictID])

  useEffect(() => {
    if (curriculaProcessing) {
      const intervalId = setInterval(() => {
        tkatEventCheck()
      }, checkProcessCompletion)

      return () => clearInterval(intervalId)
    }
  }, [checkProcessCompletion, curriculaProcessing])

  const isDisabled = () => {
    let disabled = false
    if (schoolID) {
      disabled = LockedSchoolsIds.includes(schoolID)
    } else if (districtID) {
      disabled = LockedSchoolsIds.includes(districtID)
    }
    return disabled
  }

  const isEnabled = () => {
    return curriculaMap.size <= 0 && !curriculaProcessing
  }

  const handleClose = () => {
    setOpen(false)
    setShowSelection(!showSelection)
  }

  const notify = useNotify()
  const removeCurriculum = (title: string) => {
    curriculaMap.delete(title)
    setCurriculaMap(new Map(curriculaMap))
  }

  function getCurriculaArray() {
    if (curriculaMap.size === 0) {
      return []
    }

    return Array.from(curriculaMap.values())
  }

  const confirm = async () => {
    if (getCurriculaArray().length === 0) {
      return
    }
    setOpen(true)
    setShowSelection(!showSelection)
  }

  const submitRequest = async () => {
    setCurrentDialog(null)
    const curriculaArr = getCurriculaArray()
    const resp = await addCurricula(curriculaArr, districtID, schoolID)

    if (resp.errors === undefined || resp.errors.length === 0) {
      notify(resp.data.addCurricula + ', this may take a while', {
        type: 'info',
      })
      onAdded()
    } else {
      notify(resp.errors[0].message, {
        type: 'error',
      })
    }
  }

  const getCurriculaKeyForMap = (selection: {
    treeId: string
    masterTitle: string
  }): string => {
    return selection.masterTitle + ' : ' + selection.treeId
  }

  // Build a set of selected treeIds for checkbox display
  const selectedTreeIds = useMemo(() => {
    const ids = new Set<string>()
    for (const key of curriculaMap.keys()) {
      // Key format is "masterTitle : treeId"
      const parts = key.split(' : ')
      if (parts.length >= 2) {
        ids.add(parts[parts.length - 1])
      }
    }
    return ids
  }, [curriculaMap])

  const addCurriculum = (selection: {
    treeId: string
    productId: number
    treeVersion: string
    masterTitle: string
  }) => {
    if (curriculaMap.has(getCurriculaKeyForMap(selection))) {
      removeCurriculum(getCurriculaKeyForMap(selection))
      return
    }
    if (addCurriculumOverride) {
      addCurriculumOverride(selection.masterTitle, selection.productId)
    } else {
      setCurriculaMap(
        new Map(
          curriculaMap.set(
            getCurriculaKeyForMap(selection),
            selection.productId,
          ),
        ),
      )
    }
  }

  return (
    <>
      <Box mr={2} minWidth={'50%'}>
        {currentDialog}
        <Box>
          <Button
            disabled={isDisabled()}
            onClick={() => setShowSelection(!showSelection)}
            variant="outlined"
          >
            Curriculum List
            {showSelection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Button>
        </Box>
        {showSelection && (
          <CurriculaSelection
            onSelect={addCurriculum}
            excluded={excluded}
            activatePopUp={false}
            selectedTreeIds={selectedTreeIds}
          />
        )}
      </Box>
      {!addCurriculumOverride && (
        <Box minWidth={'50%'} style={{ float: 'right' }}>
          {curriculaProcessing && (
            <b>
              There are curricula currently being added, please come back later
            </b>
          )}
          <Box>
            <Button
              disabled={isEnabled()}
              onClick={() => confirm()}
              variant="contained"
            >
              Add Curricula
            </Button>
          </Box>
          <Box>
            <b>Number of Selected Curricula: {curriculaMap.size}</b>
            <Box style={{ overflowY: 'auto', maxHeight: '180px' }}>
              {Array.from(curriculaMap.keys()).map((title) => (
                <li key={title}>
                  {title}
                  <Button
                    onClick={() => removeCurriculum(title)}
                    key={title}
                    color="primary"
                  >
                    <DeleteIcon />
                  </Button>
                </li>
              ))}
            </Box>
          </Box>
        </Box>
      )}
      <RequestDialog
        open={open}
        title={popUpText}
        content={[]}
        submitRequest={submitRequest}
        onClose={handleClose}
      />
    </>
  )
}
